import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import '../styles/Enquiries.css';

const Enquiries = () => {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;

  const { canCreate } = useAuth();

  useEffect(() => {
    loadEnquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, search]);

  const loadEnquiries = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * pageSize;
      const params = {
        limit: pageSize,
        offset: offset,
      };
      if (search) {
        params.search = search;
      }

      const response = await api.getEnquiries(params);
      if (response.data) {
        setEnquiries(response.data.enquiries || response.data);
        const total = response.data.total || response.data.length;
        setTotalPages(Math.ceil(total / pageSize));
      }
      setError('');
    } catch (err) {
      setError('Failed to load enquiries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (enquiryId) => {
    navigate(`/enquiries/view/${enquiryId}`);
  };

  return (
    <div className="content-container">
      <div className="page-header-section">
        <h1>All Enquiries</h1>
        <div className="header-actions">
          <button onClick={loadEnquiries} className="btn btn-secondary">
            Refresh
          </button>
          {canCreate('enquiries') && (
            <button onClick={() => navigate('/enquiries/new')} className="btn btn-primary">
              Add Enquiry
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="search-section">
        <input
          type="text"
          placeholder="Search enquiries..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="search-input"
        />
      </div>

      {loading ? (
        <div className="loading">Loading enquiries...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Company</th>
                  <th>Full Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Domain</th>
                  <th>Submitted At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {enquiries.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      No enquiries found
                    </td>
                  </tr>
                ) : (
                  enquiries.map((enquiry) => (
                    <tr key={enquiry.id}>
                      <td>{enquiry.id}</td>
                      <td>{enquiry.company_name}</td>
                      <td>{enquiry.full_name}</td>
                      <td>{enquiry.email}</td>
                      <td>{enquiry.mobile}</td>
                      <td className="message-cell">{enquiry.domain}</td>
                      <td>{new Date(enquiry.submitted_at).toLocaleDateString()}</td>
                      <td className="actions-cell">
                        <button
                          onClick={() => handleViewDetails(enquiry.id)}
                          className="btn btn-sm btn-primary"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="btn btn-secondary"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="btn btn-secondary"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Enquiries;
